/**
 * Explanation on demand.
 *
 * Its own module because ui.tsx and fields.tsx both need it and ui.tsx already
 * imports fields.tsx; keeping it here avoids an import cycle.
 */
import {
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * An (i) that opens its explanation on demand.
 *
 * Click, not hover: hover is unreachable on touch. The panel is portalled to
 * <body> so a scrolling ancestor cannot clip it, as Select and DatePicker do.
 */
export function InfoHint({
	children,
	label = 'What this means',
	width = 280,
}: {
	children: ReactNode;
	/** Announced to a screen reader in place of the bare "i". */
	label?: string;
	/** Panel width in px. The default suits a sentence or two; a hint holding a
	 *  table needs more or every column wraps. */
	width?: number;
}) {
	const [open, setOpen] = useState(false);
	const [box, setBox] = useState<{ top: number; left: number } | null>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const panel = useRef<HTMLDivElement>(null);
	const id = useId();

	useLayoutEffect(() => {
		if (!open) return;
		const node = trigger.current;
		if (!node) return;
		const rect = node.getBoundingClientRect();
		/* Nudged in from the right edge so a hint in a right-hand column does not
       open off screen. */
		const left = Math.min(rect.left, window.innerWidth - width - 12);
		setBox({ top: rect.bottom + 6, left: Math.max(12, left) });
	}, [open, width]);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setOpen(false);
				trigger.current?.focus();
			}
		};
		const onDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (panel.current?.contains(target)) return;
			if (trigger.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onDown);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onDown);
		};
	}, [open]);

	return (
		<>
			<button
				type="button"
				ref={trigger}
				className="infohint"
				aria-label={label}
				aria-expanded={open}
				aria-controls={open ? id : undefined}
				onClick={() => setOpen((current) => !current)}
			>
				i
			</button>
			{open &&
				box &&
				createPortal(
					<div
						id={id}
						ref={panel}
						className="infohint-panel"
						role="note"
						style={{ top: box.top, left: box.left, width }}
					>
						{children}
					</div>,
					document.body,
				)}
		</>
	);
}
